"use client";
import NavbarAuthSection from "./NavbarAuthSection";
import { useSidebar } from "@/modules/dashboard/context/SidebarContext";

import Link from "next/link";
import NavDropdown from "@/modules/web/components/NavDropdown";
import Image from "next/image";
import AvatarImage from "@/components/AvatarImage";
import { useState, useEffect, useCallback } from "react";
import AddDependantModal from "@/modules/dashboard/components/s/AddDependantModal";
import { usePathname, useRouter } from "next/navigation";
import {
  LogOut,
  LayoutDashboard,
  RefreshCcw,
  Menu,
  ChevronDown,
  ChevronUp,
  Users,
  UserPlus,
  Check,
} from "lucide-react";
import NotificationDropdown from "@/components/NotificationDropdown";
import { Languages } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useLogout } from "@/modules/auth/hooks/useLogout";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { useAuth } from "@/modules/auth/context/AuthContext";
import {
  canCreateDependant,
  getSwitchableAccounts,
  isTeacherAccount as isTeacherAccountUser,
} from "@/modules/auth/utils/userAccess";
import { switchDependantApi } from "@/modules/dashboard/services/dependants.api";
import { useSections } from "../hooks/useSections";
import { getClientRegion, getRegionLocaleHref } from "@/services/region";
import { resolveNavbarGroups } from "@/modules/web/utils/categoryGrouping";
import LoginLink from "@/modules/auth/components/LoginLink";
import type { LearningSectionItem } from "@/modules/web/types/learning";
import SiteLogo from "@/components/SiteLogo";

/** Localized display name for a category section (handles string or {ar,en}). */
function sectionDisplayName(
  section: LearningSectionItem,
  locale: string,
): string {
  if (typeof section.name === "string") {
    return section.name;
  }

  return locale === "ar" ? section.name.ar : section.name.en;
}

export default function Navbar() {
  const locale = useLocale();
  const t = useTranslations("web.navbar");
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading, isAuthenticated } = useCurrentUser();
  const auth = useAuth();
  const { handleLogout } = useLogout();
  const regionSlug = getClientRegion(pathname) || undefined;
  const { sections } = useSections(locale, regionSlug, undefined, true);
  // Each category's navbar presence (and Other-vs-standalone split) is
  // evaluated on its own flags only; a child never inherits or is hidden by
  // its parent's visibility (see resolveNavbarGroups).
  const { primary: primarySections, other: otherSections, childrenByParentId } =
    resolveNavbarGroups(sections);
  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const isArabic = locale === "ar";

  const [openCategories, setOpenCategories] = useState(false);
  const [openMore, setOpenMore] = useState(false);
  const [openSwitchAccount, setOpenSwitchAccount] = useState(false);

  const [localSwitchingId, setLocalSwitchingId] = useState<string | null>(null);
  const [showWhosLearningModal, setShowWhosLearningModal] = useState(false);
  const [isAddDependantModalOpen, setIsAddDependantModalOpen] = useState(false);

  const getProfiles = useCallback(() => {
    if (!user) return [];

    const profileUser = user as unknown as {
      id: string | number;
      name?: string;
      fname?: string;
      lname?: string;
      gender?: string;
      birthday?: string | null;
      parent_id?: string | number | null;
      parent?: {
        id: string | number;
        name?: string;
        fname?: string;
        lname?: string;
        gender?: string;
        birthday?: string | null;
        dependants?: {
          id: string | number;
          name?: string;
          fname?: string;
          lname?: string;
          gender?: string;
          birthday?: string | null;
        }[];
      };
      dependants?: {
        id: string | number;
        name?: string;
        fname?: string;
        lname?: string;
        gender?: string;
        birthday?: string | null;
      }[];
    };

    const parentProfile =
      profileUser.parent_id == null
        ? {
            id: String(profileUser.id),
            name:
              `${profileUser.fname ?? ""} ${profileUser.lname ?? ""}`.trim() ||
              String(profileUser.name || ""),
            gender: String(profileUser.gender || "male"),
            birthday: profileUser.birthday ?? null,
            isParent: true,
          }
        : profileUser.parent
          ? {
              id: String(profileUser.parent.id),
              name:
                `${profileUser.parent.fname ?? ""} ${profileUser.parent.lname ?? ""}`.trim() ||
                String(profileUser.parent.name || ""),
              gender: String(profileUser.parent.gender || "male"),
              birthday: profileUser.parent.birthday ?? null,
              isParent: true,
            }
          : null;

    const dependantsSource =
      profileUser.parent_id == null
        ? profileUser.dependants
        : profileUser.parent?.dependants;

    const dependantProfiles = (dependantsSource || []).map((dep) => ({
      id: String(dep.id),
      name:
        `${dep.fname ?? ""} ${dep.lname ?? ""}`.trim() ||
        String(dep.name || ""),
      gender: String(dep.gender || "male"),
      birthday: dep.birthday ?? null,
      isParent: false,
    }));

    return [parentProfile, ...dependantProfiles].filter(
      (p): p is NonNullable<typeof p> => p !== null,
    );
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && typeof window !== "undefined") {
      const show = sessionStorage.getItem("showWhosLearning");
      if (show === "true") {
        const profiles = getProfiles();
        if (profiles.length > 1) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setShowWhosLearningModal(true);
          sessionStorage.removeItem("showWhosLearning");
        } else if (
          user &&
          Array.isArray(user.dependants) &&
          user.dependants.length === 0
        ) {
          sessionStorage.removeItem("showWhosLearning");
        }
      }
    }
  }, [isAuthenticated, user, getProfiles]);

  /***************************************************************************** */

  useEffect(() => {
    if (showWhosLearningModal) {
      document.body.style.overflow = "hidden";
    } else if (!isSidebarOpen) {
      document.body.style.overflow = "unset";
    }

    return () => {
      if (!isSidebarOpen) {
        document.body.style.overflow = "unset";
      }
    };
  }, [showWhosLearningModal, isSidebarOpen]);

  /***************************************************************************** */

  const handleProfileSelect = async (profileId: string) => {
    if (String(user?.id) === profileId) {
      setShowWhosLearningModal(false);
      return;
    }

    try {
      if (auth) {
        await auth.switchDependant(profileId);
      } else {
        await switchDependantApi(profileId);
      }
      setShowWhosLearningModal(false);
      router.replace(localizedHref("/student"));
    } catch {
      // ignore
    }
  };

  const switchingDependantId = auth?.switchingTargetId || localSwitchingId;
  const userName =
    typeof user?.name === "string" && user.name.trim().length > 0
      ? user.name
      : t("user");
  const userEmail = typeof user?.email === "string" ? user.email : "";
  const currentLocaleLabel = locale === "en" ? "English" : "العربية";
  const localizedHref = (path: string) =>
    getRegionLocaleHref(pathname, locale, path);
  const canAddDependant = canCreateDependant(user);
  const switchableAccounts = getSwitchableAccounts(user);
  // Dependant/"Switch Profile" is a student-account-only feature; a teacher
  // account has no student side, so hide it for teachers entirely.
  const isTeacherAccount = isTeacherAccountUser(user);
  const currentProfile = user
    ? {
        id: String(user.id),
        name: userName,
        isParent: (user as Record<string, unknown>).parent_id == null,
      }
    : null;
  const allSwitchAccounts = currentProfile
    ? [currentProfile, ...switchableAccounts]
    : switchableAccounts;

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("web-sidebar-open");
    } else {
      document.body.style.overflow = "unset";
      document.body.classList.remove("web-sidebar-open");
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.classList.remove("web-sidebar-open");
    };
  }, [isSidebarOpen]);

  const handleLinkClick = () => {
    setIsSidebarOpen(false);
    setOpenCategories(false);
    setOpenMore(false);
    setOpenSwitchAccount(false);
  };

  const handleLocaleChange = (nextLocale: "en" | "ar") => {
    if (locale === nextLocale) {
      return;
    }

    handleLinkClick();
    router.replace(getLocalizedPathname(pathname, locale, nextLocale));
  };

  const handleLocaleToggle = () => {
    if (typeof window !== "undefined" && window.innerWidth < 1200) {
      const nextLocale = locale === "en" ? "ar" : "en";
      handleLocaleChange(nextLocale);
    }
  };

  const handleSwitchAccountNavigation = async (_target: "list" | "create") => {
    handleLinkClick();

    // Dependant management is a student-account-only feature reachable
    // directly; there is no role to switch into first.
    router.push(localizedHref("/student/switch-account"));
  };

  const handleAddDependant = () => {
    handleLinkClick();
    setIsAddDependantModalOpen(true);
  };

  const handleDependantSwitch = async (dependantId: string) => {
    handleLinkClick();

    if (String(user?.id) === dependantId) {
      return;
    }

    if (auth) {
      try {
        await auth.switchDependant(dependantId);
        router.replace(localizedHref("/student"));
      } catch {
        // Handle error
      }
      return;
    }

    setLocalSwitchingId(dependantId);

    try {
      await switchDependantApi(dependantId);
      router.replace(localizedHref("/student"));
    } finally {
      setLocalSwitchingId(null);
    }
  };

  return (
    <>
      {/* Sticky on the public site only - this Navbar is not used by the
          dashboard, which has its own DashboardTopBar. `sticky` (not `fixed`)
          keeps the header in normal flow, so it needs no body padding
          compensation and can't overlap the page's first section. */}
      <div className="w-full bg-white sticky top-0 z-40 shadow-md">
        <div className="max-w-[1320px] mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            {" "}
            <div className="flex items-center gap-3">
              {/* Mobile Menu Button on the Left */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 rounded-md hover:bg-nav-hover transition cursor-pointer xl:hidden"
                aria-label="Open menu"
              >
                <Menu size={22} className="text-primary" />
              </button>

              <Link
                href={localizedHref("/")}
                className="flex-shrink-0 cursor-pointer"
              >
                <SiteLogo
                  fallbackSrc="/f/images/logo.png"
                  width={150}
                  height={40}
                  className="w-auto h-10 md:h-11 object-contain"
                  priority
                />
              </Link>
            </div>
            <div className="hidden xl:flex items-center gap-8">
              <Link
                href={localizedHref("/")}
                className="!text-primary hover:!text-secondary font-medium cursor-pointer"
              >
                {t("home")}
              </Link>

              {/* Categories Dropdown  */}
              <NavDropdown
                toggleLabel={t("course_categories")}
                triggerClassName="font-medium !text-primary hover:!text-secondary"
                panelClassName="bg-white rounded-lg shadow-md border border-gray-100 p-2 w-[200px]"
                label={
                  <Link
                    href={localizedHref("/categories")}
                    className="hover:!text-secondary cursor-pointer"
                  >
                    {t("course_categories")}
                  </Link>
                }
              >
                <>
                  {primarySections.map((section, index) => (
                    <div
                      key={section.id}
                      className={index < primarySections.length - 1 ? "mb-2" : ""}
                    >
                      <CategoryMenuItem
                        section={section}
                        subs={childrenByParentId.get(section.id) ?? []}
                        locale={locale}
                        localizedHref={localizedHref}
                        variant="desktop"
                      />
                    </div>
                  ))}

                  {otherSections.length > 0 && (
                    <OtherCategoriesMenu
                      items={otherSections}
                      locale={locale}
                      localizedHref={localizedHref}
                      label={t("other")}
                      variant="desktop"
                    />
                  )}
                </>
              </NavDropdown>

              <Link
                href={localizedHref("/teachers")}
                className="!text-primary hover:!text-secondary font-medium transition cursor-pointer"
              >
                {t("teachers")}
              </Link>

              <NavDropdown
                toggleLabel={t("more")}
                triggerClassName="!text-primary hover:!text-secondary font-medium"
                panelClassName="bg-white rounded-lg shadow-md border border-gray-100 p-2 w-[170px]"
                label={<span className="font-medium">{t("more")}</span>}
              >
                  <div className="flex flex-col">
                    <Link
                      href={localizedHref("/contact-us")}
                      className="block px-3 py-2 text-[15px] !text-primary font-medium hover:bg-nav-hover rounded-md transition cursor-pointer"
                    >
                      {t("contact_us")}
                    </Link>

                    <Link
                      href={localizedHref("/about-us")}
                      className="block px-3 py-2 text-[15px] !text-primary font-medium hover:bg-nav-hover rounded-md transition cursor-pointer"
                    >
                      {t("about_us")}
                    </Link>
                    {/* <Link
                      href="/vacancies"
                      className="block px-3 py-2 text-[15px] !text-primary font-medium hover:bg-nav-hover rounded-md transition cursor-pointer"
                    >
                      {t("vacancies")}
                    </Link> */}
                    <Link
                      href={localizedHref("/terms-of-service")}
                      className="block px-3 py-2 text-[15px] !text-primary font-medium hover:bg-nav-hover rounded-md transition cursor-pointer"
                    >
                      {t("terms_of_service")}
                    </Link>
                    {/* <Link
                      href="/products"
                      className="block px-3 py-2 text-[15px] !text-primary font-medium hover:bg-nav-hover rounded-md transition cursor-pointer"
                    >
                      Products
                    </Link> */}
                    {/* <Link
                      href="/courses"
                      className="block px-3 py-2 text-[15px] !text-primary font-medium hover:bg-nav-hover rounded-md transition cursor-pointer"
                    >
                      Courses
                    </Link> */}
                    {/* <Link
                      href="/library"
                      className="block px-3 py-2 text-[15px] !text-primary font-medium hover:bg-nav-hover rounded-md transition cursor-pointer"
                    >
                      Library
                    </Link> */}
                  </div>
              </NavDropdown>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden xl:flex items-center gap-2">
                <NavbarAuthSection
                  loading={loading}
                  isAuthenticated={isAuthenticated}
                  user={user}
                  userName={userName}
                  localizedHref={localizedHref}
                  switchableAccounts={allSwitchAccounts}
                  switchingDependantId={switchingDependantId}
                  handleDependantSwitch={handleDependantSwitch}
                  canCreateDependant={canAddDependant}
                  isTeacherAccount={isTeacherAccount}
                  handleSwitchAccountNavigation={handleSwitchAccountNavigation}
                  onAddDependant={handleAddDependant}
                  handleLogout={handleLogout}
                />

                <div className="h-6 w-px bg-gray-200"></div>

                {/* Booking is not available to Teacher accounts. */}
                {!isTeacherAccount && (
                  <Link
                    href={localizedHref("/book/free-trial")}
                    className="min-w-[110px] bg-nav-button text-on-nav-button font-semibold px-6 py-2.5 rounded-[10px] hover:bg-nav-button-hover transition text-center text-[14px] cursor-pointer shadow-sm"
                  >
                    {t("free_trial")}
                  </Link>
                )}

                <NavDropdown
                  align="end"
                  toggleLabel={currentLocaleLabel}
                  triggerClassName="ml-1 rounded-md px-3 py-2 text-primary font-medium text-sm transition-colors duration-200 hover:bg-nav-hover"
                  panelClassName="w-[110px] rounded-md border border-gray-100 bg-white p-2 shadow-md"
                  label={
                    <span className="flex items-center gap-2">
                      <Languages size={16} className="text-secondary" />
                      <span>{currentLocaleLabel}</span>
                    </span>
                  }
                >
                  <button
                    className="block w-full px-2 py-2 text-left hover:bg-nav-hover !text-primary cursor-pointer rounded-[10px]"
                    onClick={() => handleLocaleChange("en")}
                    type="button"
                  >
                    English
                  </button>
                  <button
                    className="block w-full px-2 py-2 text-left hover:bg-nav-hover !text-primary cursor-pointer rounded-[10px]"
                    onClick={() => handleLocaleChange("ar")}
                    type="button"
                  >
                    العربية
                  </button>
                </NavDropdown>
              </div>

              <div className="flex xl:hidden items-center gap-2">
                {/* Below xl this is a direct toggle rather than a menu: there
                    are only two locales, so one tap is better than two. The
                    hover-only list that used to sit behind this button could
                    never open on a touch device anyway. */}
                <button
                  onClick={handleLocaleToggle}
                  type="button"
                  aria-label={t("switch_language")}
                  title={t("switch_language")}
                  className="flex items-center gap-1 rounded text-primary text-sm px-2 py-2 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
                >
                  <Languages size={16} className="text-secondary" />
                  <span className="font-medium hidden sm:inline">
                    {currentLocaleLabel}
                  </span>
                </button>

                {!loading && isAuthenticated ? <NotificationDropdown /> : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==========     (Mobile Sidebar Menu) ========== */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 xl:hidden ${
          isSidebarOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setIsSidebarOpen(false)}
      />

      <div
        className={`fixed top-0 start-0 h-dvh w-[85%] max-w-[320px] bg-white shadow-2xl z-[60] transition-transform duration-300 ease-out xl:hidden ${
          isSidebarOpen
            ? "translate-x-0"
            : isArabic
              ? "translate-x-full"
              : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
          {!loading && isAuthenticated ? (
            <>
              <div className="p-2 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <AvatarImage
                    src={user?.image as string | undefined}
                    alt={userName}
                    width={48}
                    height={48}
                    className="rounded-full object-cover border border-gray-200 w-12 h-12"
                  />
                  <div>
                    <p className="font-semibold text-primary">{userName}</p>
                    <p className="text-xs text-gray-500">{userEmail}</p>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100">
                <Link
                  href={localizedHref("/dashboard")}
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-4 py-3 !text-primary hover:bg-nav-hover font-medium transition cursor-pointer"
                >
                  <LayoutDashboard size={18} color="var(--secondary)" fill="var(--secondary)" />
                  {t("dashboard")}
                </Link>
              </div>

              {!isTeacherAccount && (
              <div className="border-b border-gray-100">
                {/* The whole row toggles the submenu. It used to be a link
                    plus a separate chevron, which on touch meant tapping
                    anywhere but the 16px chevron navigated away and closed the
                    drawer instead of expanding it. The destination now lives
                    inside the submenu. */}
                <button
                  onClick={() => setOpenSwitchAccount(!openSwitchAccount)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-primary hover:bg-nav-hover font-medium transition cursor-pointer"
                  type="button"
                  aria-expanded={openSwitchAccount}
                >
                  <RefreshCcw size={18} color="var(--secondary)" />
                  <span className="flex-1 text-start">
                    {t("switch_account")}
                  </span>
                  {openSwitchAccount ? (
                    <ChevronUp size={16} className="text-secondary" />
                  ) : (
                    <ChevronDown size={16} className="text-secondary" />
                  )}
                </button>

                {openSwitchAccount && (
                  <div className="bg-gray-50/50 pl-11 py-2 space-y-1">
                    {allSwitchAccounts.map((account, index) => {
                      const isActive = account.id === String(user?.id);
                      return (
                        <button
                          key={account.id}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition text-left disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer ${
                            isActive
                              ? "bg-gray-100 text-nav-text font-bold"
                              : "text-primary hover:bg-nav-hover"
                          }`}
                          disabled={switchingDependantId !== null}
                          onClick={() => void handleDependantSwitch(account.id)}
                          type="button"
                        >
                          <Users size={14} color="var(--secondary)" />
                          <span className="flex-1 min-w-0 flex items-center gap-1.5 truncate">
                            {switchingDependantId === account.id
                              ? t("switching")
                              : resolveDependantName(account.name, index)}
                            {account.isParent && (
                              <span
                                className="shrink-0 rounded-full px-1.5 py-[1px] text-[9px] font-bold"
                                style={{ background: "color-mix(in oklab, var(--secondary) 15%, transparent)", color: "var(--secondary)" }}
                              >
                                {t("primary")}
                              </span>
                            )}
                          </span>
                          {isActive && (
                            <Check size={14} className="text-secondary shrink-0" />
                          )}
                        </button>
                      );
                    })}
                    {canAddDependant ? (
                      <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-primary hover:bg-nav-hover rounded-md transition text-left cursor-pointer"
                        onClick={handleAddDependant}
                        type="button"
                      >
                        <UserPlus size={14} color="var(--secondary)" />
                        {t("add_new_dependant")}
                      </button>
                    ) : null}
                    <Link
                      href={localizedHref("/student/switch-account")}
                      onClick={handleLinkClick}
                      className="w-full flex items-center gap-2 px-3 py-2 !text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                    >
                      <RefreshCcw size={14} color="var(--secondary)" />
                      {t("view_all_profiles")}
                    </Link>
                  </div>
                )}
              </div>
              )}
            </>
          ) : !loading ? (
            <div className="p-4 border-b border-gray-100">
              <LoginLink
                loginHref={localizedHref("/login")}
                onClick={handleLinkClick}
                className="block w-full text-center bg-gray-100 text-nav-text font-medium px-6 py-3 rounded-md hover:bg-nav-hover transition cursor-pointer"
              >
                {t("login")}
              </LoginLink>
            </div>
          ) : null}

          <div>
            {/* Home */}
            <Link
              href={localizedHref("/")}
              onClick={handleLinkClick}
              className="block px-4 py-3 !text-primary hover:bg-nav-hover font-medium transition border-b border-gray-100 cursor-pointer"
            >
              {t("home")}
            </Link>

            {/* Categories Section      */}
            <div className="border-b border-gray-100">
              {/* Toggle-only row, same as "More" below: the label used to be a
                  link to /categories, so tapping the row navigated and closed
                  the drawer rather than revealing the sections. */}
              <button
                onClick={() => setOpenCategories(!openCategories)}
                type="button"
                aria-expanded={openCategories}
                className="w-full flex items-center justify-between px-4 py-3 text-primary hover:bg-nav-hover font-medium transition cursor-pointer"
              >
                <span>{t("course_categories")}</span>
                {openCategories ? (
                  <ChevronUp size={16} className="text-secondary" />
                ) : (
                  <ChevronDown size={16} className="text-secondary" />
                )}
              </button>

              {openCategories && (
                <div className="bg-gray-50/50 pl-4 py-2 space-y-1">
                  <Link
                    href={localizedHref("/categories")}
                    onClick={handleLinkClick}
                    className="block px-3 py-2 !text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                  >
                    {t("view_all_categories")}
                  </Link>
                  {primarySections.map((section) => (
                    <CategoryMenuItem
                      key={section.id}
                      section={section}
                      subs={childrenByParentId.get(section.id) ?? []}
                      locale={locale}
                      localizedHref={localizedHref}
                      variant="mobile"
                      onNavigate={handleLinkClick}
                    />
                  ))}

                  {otherSections.length > 0 && (
                    <OtherCategoriesMenu
                      items={otherSections}
                      locale={locale}
                      localizedHref={localizedHref}
                      label={t("other")}
                      variant="mobile"
                      onNavigate={handleLinkClick}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Teachers */}
            <Link
              href={localizedHref("/teachers")}
              onClick={handleLinkClick}
              className="block px-4 py-3 !text-primary hover:bg-nav-hover font-medium transition border-b border-gray-100 cursor-pointer"
            >
              {t("teachers")}
            </Link>

            {/* More Section      */}
            <div className="border-b border-gray-100">
              <button
                onClick={() => setOpenMore(!openMore)}
                className="w-full px-4 py-3 flex items-center justify-between text-primary hover:bg-nav-hover font-medium transition cursor-pointer"
              >
                <span>{t("more")}</span>
                {openMore ? (
                  <ChevronUp size={16} className="text-secondary" />
                ) : (
                  <ChevronDown size={16} className="text-secondary" />
                )}
              </button>

              {openMore && (
                <div className="bg-gray-50/50 pl-4 py-2 space-y-1">
                  {/* <Link
                    href="/courses"
                    onClick={handleLinkClick}
                    className="block px-3 py-2 text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                  >
                    Courses
                  </Link> */}
                  <Link
                    href={localizedHref("/contact-us")}
                    onClick={handleLinkClick}
                    className="block px-3 py-2 text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                  >
                    {t("contact_us")}
                  </Link>
                  <Link
                    href={localizedHref("/about-us")}
                    onClick={handleLinkClick}
                    className="block px-3 py-2 text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                  >
                    {t("about_us")}
                  </Link>

                  <Link
                    href={localizedHref("/vacancies")}
                    onClick={handleLinkClick}
                    className="block px-3 py-2 text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                  >
                    {t("vacancies")}
                  </Link>
                  <Link
                    href={localizedHref("/terms-of-service")}
                    onClick={handleLinkClick}
                    className="block px-3 py-2 text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                  >
                    {t("terms_of_service")}
                  </Link>
                  {/* <Link
                    href="/products"
                    onClick={handleLinkClick}
                    className="block px-3 py-2 text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                  >
                    Products
                  </Link> */}

                  {/* <Link
                    href="/library"
                    onClick={handleLinkClick}
                    className="block px-3 py-2 text-primary hover:bg-nav-hover rounded-md transition cursor-pointer"
                  >
                    Library
                  </Link> */}
                </div>
              )}
            </div>
          </div>
          </div>

          <div className="p-4 border-t border-gray-100">
            {!loading && isAuthenticated ? (
              <button
                className="flex items-center gap-3 w-full px-4 py-3 text-[#D90000] hover:bg-red-50 font-medium rounded-lg transition cursor-pointer"
                onClick={async () => {
                  handleLinkClick();
                  await handleLogout();
                }}
                type="button"
              >
                <LogOut size={18} color="#D90000" />
                {t("logout")}
              </button>
            ) : null}

            {/*  Free Trial — hidden for Teacher accounts, who can't book. */}
            {!isTeacherAccount && (
              <Link
                href={localizedHref("/book/free-trial")}
                onClick={handleLinkClick}
                className="block text-center mt-3 bg-nav-button text-white font-medium px-4 py-2 rounded-md hover:bg-primary-hover transition cursor-pointer"
              >
                {t("free_trial")}
              </Link>
            )}
          </div>
        </div>
      </div>

      {showWhosLearningModal && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black/70 backdrop-blur-sm animate-fadeIn">
          <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
            <div className="bg-[rgb(51,51,51)] rounded-xl sm:rounded-2xl shadow-2xl p-5 sm:p-6 md:p-6 w-full max-w-[360px] sm:max-w-lg md:max-w-xl mx-auto relative max-h-[85vh] overflow-y-auto">
              <h3 className="text-xl sm:text-2xl md:text-2xl text-white font-bold text-center mb-1.5 sm:mb-2">
                {t("whos_learning")}
              </h3>
              <p className="text-gray-400 text-center text-xs sm:text-sm mb-5 sm:mb-6 md:mb-6">
                {t("select_profile_desc")}
              </p>
              <div className="flex flex-wrap justify-center gap-x-2 gap-y-4 sm:gap-x-3 sm:gap-y-5 md:gap-x-4 md:gap-y-4">
                {getProfiles().map(
                  (
                    profile: {
                      id: string;
                      name: string;
                      gender: string;
                      birthday: string | null;
                      isParent: boolean;
                    },
                    index: number,
                  ) => {
                    const isActive = String(user?.id) === profile.id;
                    const avatarSrc = getProfileAvatarSrc(
                      profile.gender,
                      profile.birthday,
                    );
                    const avatarBg =
                      PROFILE_AVATAR_PALETTE[
                        index % PROFILE_AVATAR_PALETTE.length
                      ];
                    return (
                      <button
                        key={profile.id}
                        onClick={() => void handleProfileSelect(profile.id)}
                        className="w-[28%] min-w-[72px] sm:w-[25%] sm:min-w-[88px] flex flex-col items-center transition-transform duration-300 hover:scale-105 cursor-pointer group"
                      >
                        <div
                          className={`w-14 h-14 sm:w-20 sm:h-20 md:w-20 md:h-20 rounded-xl sm:rounded-2xl overflow-hidden relative flex items-center justify-center ring-2 transition shrink-0 ${
                            isActive
                              ? "ring-white"
                              : "ring-transparent group-hover:ring-white/40"
                          } ${avatarBg}`}
                        >
                          <Image
                            src={avatarSrc}
                            alt={profile.name}
                            fill
                            sizes="(min-width: 768px) 96px, (min-width: 640px) 80px, 56px"
                            className="object-contain p-2 sm:p-2.5 md:p-3"
                          />
                        </div>
                        <span className="font-semibold text-white text-center truncate w-full mt-1.5 sm:mt-2 text-[11px] sm:text-sm">
                          {profile.name}
                        </span>
                        {isActive && (
                          <span className="text-[9px] sm:text-[10px] mt-0.5 font-bold text-[#F5A623] uppercase tracking-wider">
                            {t("current_profile")}
                          </span>
                        )}
                      </button>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AddDependantModal
        isOpen={isAddDependantModalOpen}
        onClose={() => setIsAddDependantModalOpen(false)}
      />
    </>
  );
}

const PROFILE_AVATAR_PALETTE = [
  "bg-[#6C4FE0]",
  "bg-[#1FB58A]",
  "bg-[#F5A623]",
  "bg-[#E5484D]",
  "bg-[#1E6FE0]",
  "bg-[#1FB58A]",
];

function getProfileAge(birthday: string | null | undefined): number | null {
  if (!birthday) return null;

  const birthDate = new Date(birthday);
  if (Number.isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasHadBirthdayThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());
  if (!hasHadBirthdayThisYear) age -= 1;

  return age;
}

function getProfileAvatarSrc(
  gender: string,
  birthday: string | null | undefined,
): string {
  const age = getProfileAge(birthday);
  const isAdult = age !== null && age >= 14;
  const isMale = gender === "male";

  if (isAdult) {
    return isMale ? "/f/images/man.png" : "/f/images/woman.png";
  }
  return isMale ? "/f/images/boy.png" : "/f/images/girl.png";
}

function getLocalizedPathname(
  pathname: string,
  currentLocale: string,
  nextLocale: string,
) {
  const segments = pathname.split("/");

  if (segments[1] === currentLocale) {
    segments[1] = nextLocale;
  } else if (segments[2] === currentLocale) {
    segments[2] = nextLocale;
  } else {
    if (pathname === `/${currentLocale}`) return `/${nextLocale}`;
    if (pathname.startsWith(`/${currentLocale}/`))
      return pathname.replace(`/${currentLocale}`, `/${nextLocale}`);
    return `/${nextLocale}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
  }

  return segments.join("/");
}

function resolveDependantName(name: string, index: number): string {
  if (name.trim().length > 0) {
    return name;
  }

  return `#${index + 1}`;
}

/**
 * A single top-level category in the Navbar menu. Always a link to its own
 * category page; when it has subs (other categories assigned to it as their
 * Parent Category), an expand toggle next to the link reveals them. Shared by
 * the desktop dropdown and the mobile accordion via the `variant` prop.
 */
function CategoryMenuItem({
  section,
  subs,
  locale,
  localizedHref,
  variant,
  onNavigate,
}: {
  section: LearningSectionItem;
  subs: LearningSectionItem[];
  locale: string;
  localizedHref: (path: string) => string;
  variant: "desktop" | "mobile";
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const isDesktop = variant === "desktop";
  const hasSubs = subs.length > 0;

  const linkClassName = isDesktop
    ? "block flex-1 font-medium px-3 py-2 text-[15px] !text-[#002A68] hover:bg-gray-50 transition-all duration-200 rounded-md cursor-pointer"
    : "block flex-1 px-3 py-2 !text-[#002A68] hover:bg-gray-100 rounded-md transition cursor-pointer";

  const subItemClassName = isDesktop
    ? "block font-medium px-3 py-2 text-[15px] !text-[#002A68] hover:bg-gray-50 transition-all duration-200 rounded-md cursor-pointer"
    : "block px-3 py-2 !text-[#002A68] hover:bg-gray-100 rounded-md transition cursor-pointer";

  return (
    <div>
      <div className="flex items-center">
        <Link
          href={localizedHref(`/categories/${section.slug}`)}
          onClick={onNavigate}
          className={linkClassName}
        >
          {sectionDisplayName(section, locale)}
        </Link>
        {hasSubs && (
          <button
            type="button"
            aria-expanded={open}
            aria-label={sectionDisplayName(section, locale)}
            onClick={() => setOpen((v) => !v)}
            className="px-2 py-2 text-[#42ADD8] cursor-pointer"
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
      </div>

      {hasSubs && open && (
        <div className={isDesktop ? "mt-1 pl-3" : "pl-4"}>
          {subs.map((sub) => (
            <Link
              key={sub.id}
              href={localizedHref(`/categories/${sub.slug}`)}
              onClick={onNavigate}
              className={subItemClassName}
            >
              {sectionDisplayName(sub, locale)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Trailing "Other" entry in the categories menu: a toggle that reveals the
 * categories an admin flagged `show_in_other`, in their configured order.
 * Rendered only when there is at least one such category (the caller guards on
 * `items.length`). Shared by the desktop dropdown and the mobile accordion via
 * the `variant` prop, which only swaps the surrounding padding/indent classes.
 */
function OtherCategoriesMenu({
  items,
  locale,
  localizedHref,
  label,
  variant,
  onNavigate,
}: {
  items: LearningSectionItem[];
  locale: string;
  localizedHref: (path: string) => string;
  label: string;
  variant: "desktop" | "mobile";
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const isDesktop = variant === "desktop";

  const triggerClassName = isDesktop
    ? "flex w-full items-center justify-between font-medium px-3 py-2 text-[15px] !text-primary hover:bg-nav-hover transition-all duration-200 rounded-md cursor-pointer"
    : "flex w-full items-center justify-between px-3 py-2 !text-primary hover:bg-nav-hover rounded-md transition cursor-pointer";

  const itemClassName = isDesktop
    ? "block font-medium px-3 py-2 text-[15px] !text-primary hover:bg-nav-hover transition-all duration-200 rounded-md cursor-pointer"
    : "block px-3 py-2 !text-primary hover:bg-nav-hover rounded-md transition cursor-pointer";

  return (
    <div className={isDesktop ? "mt-2 border-t border-gray-100 pt-2" : ""}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerClassName}
      >
        <span>{label}</span>
        {open ? (
          <ChevronUp size={16} className="text-secondary" />
        ) : (
          <ChevronDown size={16} className="text-secondary" />
        )}
      </button>

      {open && (
        <div className={isDesktop ? "mt-1 pl-3" : "pl-4"}>
          {items.map((section) => (
            <Link
              key={section.id}
              href={localizedHref(`/categories/${section.slug}`)}
              onClick={onNavigate}
              className={itemClassName}
            >
              {sectionDisplayName(section, locale)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
